import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {PickerOptionRow} from '@/components/employee-picker'
import {EMPLOYMENT_TYPES} from '@/components/employee-form-dialog'
import {ConfirmarAltaDialog} from '@/components/confirmar-identidad-dialog'
import {avisarSiQuedaEnVerificacion, pideConfirmarIdentidad} from '@/hooks/platform/use-access'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {bigramSimilarity, rankMatches} from '@/lib/ranking'
import {validateCedula} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

function digits(text: string): string {
  return text.replace(/\D/g, '')
}

function editDistanceLeOne(a: string, b: string): boolean {
  if (a === b) return true
  if (Math.abs(a.length - b.length) > 1) return false
  let i = 0
  while (i < a.length && i < b.length && a[i] === b[i]) i++
  if (a.length === b.length) return a.slice(i + 1) === b.slice(i + 1)
  const [longer, shorter] = a.length > b.length ? [a, b] : [b, a]
  return longer.slice(i + 1) === shorter.slice(i)
}

function adjacentTransposition(a: string, b: string): boolean {
  if (a.length !== b.length || a === b) return false
  let i = 0
  while (i < a.length && a[i] === b[i]) i++
  if (i >= a.length - 1) return false
  if (a[i] !== b[i + 1] || a[i + 1] !== b[i]) return false
  return a.slice(i + 2) === b.slice(i + 2)
}

export type Person = Pick<Employee, 'id' | 'name' | 'last_name' | 'cedula'> & {
  status?: Employee['status']
  // `externo` es un externo con acceso: una membresía real, no solo un nombre.
  vinculo?: Employee['vinculo']
}

export function PersonPicker({
  businessId,
  roster,
  value,
  onChange,
}: {
  businessId: string
  roster: Employee[]
  value: Person | null
  onChange: (person: Person | null) => void
}) {
  const {t} = useTranslation()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [cedula, setCedula] = useState('')
  const [cedulaError, setCedulaError] = useState<string | undefined>()
  const [employmentType, setEmploymentType] = useState<(typeof EMPLOYMENT_TYPES)[number]>('empleado')
  const [pending, setPending] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  const matches = useMemo(() => rankMatches(roster, query), [roster, query])

  const typedCedula = digits(cedula)
  const exactMatch = useMemo(
    () => (typedCedula ? roster.find((e) => digits(e.cedula) === typedCedula) : undefined),
    [roster, typedCedula]
  )
  const nearMatch = useMemo(() => {
    if (!typedCedula || exactMatch) return undefined
    const typedName = `${name} ${lastName}`.trim()
    if (!typedName) return undefined
    return roster.find((e) => {
      const known = digits(e.cedula)
      const cedulaClose = editDistanceLeOne(known, typedCedula) || adjacentTransposition(known, typedCedula)
      return cedulaClose && bigramSimilarity(typedName, `${e.name} ${e.last_name}`) >= 0.5
    })
  }, [roster, typedCedula, exactMatch, name, lastName])

  function reset() {
    setQuery('')
    setCreating(false)
    setServerError(null)
  }

  function pick(person: Employee) {
    onChange({...person})
    reset()
  }

  async function createPerson(confirmar_identidad: boolean) {
    setPending(true)
    setServerError(null)
    setConfirmando(false)
    try {
      const created = await api.post<Employee>(`/businesses/${businessId}/employees`, {
        cedula: typedCedula,
        name: name.trim(),
        last_name: lastName.trim(),
        role: 'user',
        employment_type: employmentType,
        confirmar_identidad,
      })
      avisarSiQuedaEnVerificacion(created.data, t)
      pick(created.data)
      void queryClient.invalidateQueries({queryKey: ['employees', businessId]})
    } catch (err) {
      if (pideConfirmarIdentidad(err)) setConfirmando(true)
      else setServerError(translateApiError(err))
    } finally {
      setPending(false)
    }
  }

  if (value) {
    return (
      <button
        type="button"
        data-testid="person-chip"
        onClick={() => onChange(null)}
        className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground hover:opacity-75">
        <span className="min-w-0 truncate">
          {value.name} {value.last_name}
        </span>
        <span className="shrink-0 font-mono font-normal opacity-70">{value.cedula}</span>
        {value.vinculo === 'externo' && (
          <span className="shrink-0 font-normal opacity-70" data-testid="person-chip-externo">
            · {t('plan.externoTag')}
          </span>
        )}
        <span aria-hidden="true" className="shrink-0 font-normal opacity-60">
          ×
        </span>
      </button>
    )
  }

  if (creating) {
    const canSave = !exactMatch && name.trim() && lastName.trim() && typedCedula
    return (
      <div className="space-y-3 rounded-lg border border-border bg-secondary/40 p-3">
        <button
          type="button"
          onClick={() => setCreating(false)}
          className="font-mono text-[11px] text-primary hover:underline">
          {t('plan.picker.back')}
        </button>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="person_name">{t('plan.picker.name')}</Label>
            <Input id="person_name" value={name} maxLength={tope('NOM')} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="person_last_name">{t('plan.picker.lastName')}</Label>
            <Input
              id="person_last_name"
              value={lastName}
              maxLength={tope('NOM')}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="person_cedula">{t('plan.picker.cedula')}</Label>
            <Input
              id="person_cedula"
              value={cedula}
              maxLength={10}
              className="font-mono"
              aria-invalid={!!cedulaError}
              onChange={(e) => {
                setCedula(e.target.value)
                setCedulaError(undefined)
              }}
              onBlur={(e) =>
                setCedulaError((e.target.value.trim() ? validateCedula(e.target.value) : null) ?? undefined)
              }
            />
            <FieldError error={cedulaError} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="person_employment_type">{t('employees.fields.employmentType')}</Label>
            <select
              id="person_employment_type"
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value as (typeof EMPLOYMENT_TYPES)[number])}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              {EMPLOYMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`employees.employmentType.${type}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {exactMatch && (
          <div className="tone-red space-y-2 rounded-lg px-3 py-2 text-sm" data-testid="person-dup-exact">
            <p>
              <span className="font-semibold">{t('plan.picker.exactTitle')}</span>{' '}
              {t('plan.picker.exactBody', {
                name: `${exactMatch.name} ${exactMatch.last_name}`,
                cedula: exactMatch.cedula,
              })}
            </p>
            <Button type="button" size="sm" onClick={() => pick(exactMatch)}>
              {t('plan.picker.useExisting')}
            </Button>
          </div>
        )}
        {nearMatch && (
          <div className="notice-amber space-y-2 rounded-lg px-3 py-2 text-sm" data-testid="person-dup-near">
            <p>
              <span className="font-semibold">{t('plan.picker.nearTitle')}</span>{' '}
              {t('plan.picker.nearBody', {name: `${nearMatch.name} ${nearMatch.last_name}`, cedula: nearMatch.cedula})}
            </p>
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={() => pick(nearMatch)}>
                {t('plan.picker.useExisting')}
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={pending} onClick={() => createPerson(false)}>
                {t('plan.picker.createAnyway')}
              </Button>
            </div>
          </div>
        )}
        {serverError && <p className="text-sm text-destructive">{serverError}</p>}
        {!nearMatch && (
          <Button type="button" size="sm" disabled={!canSave || pending} onClick={() => createPerson(false)}>
            {t('plan.picker.save')}
          </Button>
        )}
        <ConfirmarAltaDialog
          open={confirmando}
          onOpenChange={setConfirmando}
          nombre={`${name.trim()} ${lastName.trim()}`}
          cedula={typedCedula}
          onConfirm={() => createPerson(true)}
          pending={pending}
        />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <input
        data-testid="person-picker-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('plan.picker.placeholder')}
        className="w-full rounded-lg border border-input bg-transparent px-3 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      />
      {query.trim() && (
        <div className="overflow-hidden rounded-lg border border-border bg-popover shadow-md">
          <p className="px-3 pt-2 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
            {t('plan.picker.employees')}
          </p>
          <ul>
            {matches.map((employee) => (
              <li key={employee.id}>
                <PickerOptionRow person={employee} testId="person-option" onPick={() => pick(employee)} />
              </li>
            ))}
          </ul>
          {matches.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t('plan.picker.noResults')}</p>
          )}
        </div>
      )}
      <button
        type="button"
        data-testid="person-create"
        onClick={() => {
          setCreating(true)
          setServerError(null)
        }}
        className="font-mono text-[11px] text-primary hover:underline">
        {t('plan.picker.create')}
      </button>
    </div>
  )
}
